// JSA V1 HazCom Info Components
import React, { useEffect } from "react";
import styles from "./jsaHazComInfoComponents.module.css";

// Helper function to get field error for HazCom fields
// Note: Currently HazCom section only has checkboxes, but this is included for consistency
// and future extensibility if text inputs are added
const getHazComFieldError = (autosaveErrors, fieldName) => {
  const key = `jsa_haz_com.${fieldName}`;
  return autosaveErrors?.fieldErrors?.[key] || null;
};

// JSA V1 HazCom Yes/No section
export const HazComYesNoSection = ({ callbacks }) => {
  // Handling the hazcome sections for checking yes
  const handleHazComYes = (e) => {
    if (!e.target.checked) {
      const confirm = window.confirm(
        "By unchecking this, all hazcom information will be deleted. Click OK to continue or Cancel to keep the data."
      );
      if (confirm) {
        callbacks.v1Actions.updateJsaHazComYes(
          callbacks.jsaData,
          e.target.checked,
          callbacks.emitPatch
        );
      }
    } else {
      callbacks.v1Actions.updateJsaHazComYes(
        callbacks.jsaData,
        e.target.checked,
        callbacks.emitPatch
      );
    }
  };

  // Handling the hazcome sections for checking no
  const handleHazComNo = (e) => {
    if (e.target.checked && callbacks.jsaV1.hazCom.showHazComSection) {
      const confirm = window.confirm(
        "By checking this, all hazcom information will be deleted. Click OK to continue or Cancel to keep the data."
      );
      if (confirm) {
        callbacks.v1Actions.updateJsaHazComNo(
          callbacks.jsaData,
          e.target.checked,
          callbacks.emitPatch
        );
      }
    } else {
      callbacks.v1Actions.updateJsaHazComNo(
        callbacks.jsaData,
        e.target.checked,
        callbacks.emitPatch
      );
    }
  };

  return (
    <div className={styles.hazComInfoSection}>
      <div className={styles.hazComRow}>
        <div className={styles.hazComRowText}>
          <p>Will anyone be using any chemical product?</p>
        </div>
      </div>
      <div className={styles.hazComRow}>
        <div className={styles.hazComRowInputContainer}>
          <div className={styles.hazComRowInput}>
            <label className={styles.hazComRowLabel}>Yes</label>
            <input
              type="checkbox"
              checked={callbacks.jsaData.jsaHazCom.hazComUseYes}
              onChange={handleHazComYes}
              className={styles.hazComRowInputCheckbox}
              disabled={callbacks.jsaMode === "view"}
            />
          </div>
          <div className={styles.hazComRowInput}>
            <label className={styles.hazComRowLabel}>No</label>
            <input
              type="checkbox"
              checked={callbacks.jsaData.jsaHazCom.hazComUseNo}
              onChange={handleHazComNo}
              className={styles.hazComRowInputCheckbox}
              disabled={callbacks.jsaMode === "view"}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// JSA V1 HazCom SDS Yes/No section
export const HazComSdsYesNoSection = ({ callbacks }) => {
  // Handling the Hazcom SDS yes
  const handleHazComSdsYes = (e) => {
    callbacks.v1Actions.updateJsaHazComSdsYes(
      callbacks.jsaData,
      e.target.checked,
      callbacks.emitPatch
    );
  };

  // Handling the Hazcom SDS no
  const handleHazComSdsNo = (e) => {
    callbacks.v1Actions.updateJsaHazComSdsNo(
      callbacks.jsaData,
      e.target.checked,
      callbacks.emitPatch
    );
  };

  return (
    <div className={styles.hazComInfoSection}>
      <div className={styles.hazComRow}>
        <div className={styles.hazComRowText}>
          <p>Is an SDS readily available and reviewed?</p>
        </div>
      </div>
      <div className={styles.hazComRow}>
        <div className={styles.hazComRowInputContainer}>
          <div className={styles.hazComRowInput}>
            <label className={styles.hazComRowLabel}>Yes</label>
            <input
              type="checkbox"
              checked={callbacks.jsaData.jsaHazCom.hazComSdsYes}
              onChange={handleHazComSdsYes}
              className={styles.hazComRowInputCheckbox}
              disabled={callbacks.jsaMode === "view"}
            />
          </div>
          <div className={styles.hazComRowInput}>
            <label className={styles.hazComRowLabel}>No</label>
            <input
              type="checkbox"
              checked={callbacks.jsaData.jsaHazCom.hazComSdsNo}
              onChange={handleHazComSdsNo}
              className={styles.hazComRowInputCheckbox}
              disabled={callbacks.jsaMode === "view"}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// JSA V1 HazCom Gas Meter Yes/No section
export const HazComGasMeterYesNoSection = ({ callbacks }) => {
  // Handling Gas Meter Yes
  const handleGasMeterYes = (e) => {
    callbacks.v1Actions.updateJsaHazComGasMeterYes(
      callbacks.jsaData,
      e.target.checked,
      callbacks.emitPatch
    );
  };

  // Handling Gas Meter No
  const handleGasMeterNo = (e) => {
    callbacks.v1Actions.updateJsaHazComGasMeterNo(
      callbacks.jsaData,
      e.target.checked,
      callbacks.emitPatch
    );
  };

  return (
    <div className={styles.hazComInfoSection}>
      <div className={styles.hazComRow}>
        <div className={styles.hazComRowText}>
          <p>Is a gas meter readily available and reviewed?</p>
        </div>
      </div>
      <div className={styles.hazComRow}>
        <div className={styles.hazComRowInputContainer}>
          <div className={styles.hazComRowInput}>
            <label className={styles.hazComRowLabel}>Yes</label>
            <input
              type="checkbox"
              checked={callbacks.jsaData.jsaHazCom.hazComGasMeterYes}
              onChange={handleGasMeterYes}
              className={styles.hazComRowInputCheckbox}
              disabled={callbacks.jsaMode === "view"}
            />
          </div>
          <div className={styles.hazComRowInput}>
            <label className={styles.hazComRowLabel}>No</label>
            <input
              type="checkbox"
              checked={callbacks.jsaData.jsaHazCom.hazComGasMeterNo}
              onChange={handleGasMeterNo}
              className={styles.hazComRowInputCheckbox}
              disabled={callbacks.jsaMode === "view"}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// JSA V1 HazCom Info Tab
export const JsaHazComInfoTab = ({ callbacks }) => {
  // HazCom Section - Show when "Yes" is checked for using chemical products
  useEffect(() => {
    if (callbacks.jsaData.jsaHazCom.hazComUseYes) {
      if (!callbacks.jsaV1.hazCom.showHazComSection) {
        callbacks.v1Actions.updateJsaHazComYes(callbacks.jsaData, true);
      }
    } else if (callbacks.jsaData.jsaHazCom.hazComUseNo) {
      if (callbacks.jsaV1.hazCom.showHazComSection) {
        callbacks.v1Actions.updateJsaHazComNo(callbacks.jsaData, true);
      }
    }
  }, [
    callbacks.jsaData.jsaHazCom.hazComUseYes,
    callbacks.jsaData.jsaHazCom.hazComUseNo,
    callbacks.jsaV1.hazCom.showHazComSection,
  ]);

  return (
    <div className={styles.jsaHazComInfoTab}>
      <HazComYesNoSection callbacks={callbacks} />
      <div
        className={`${styles.hazComConditionalSections} ${
          callbacks.jsaV1.hazCom.showHazComSection
            ? styles.hazComConditionalSectionsVisible
            : ""
        }`}
      >
        <HazComSdsYesNoSection callbacks={callbacks} />
        <HazComGasMeterYesNoSection callbacks={callbacks} />
      </div>
    </div>
  );
};
